import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import useRedirectLoggedOutUser from "../customHook/useRedirectLoggedOutUser";
import axios from 'axios';
import { toast } from 'react-toastify';
import './UserLocationManagement.css';
import { useNavigate } from 'react-router-dom';

const UserLocationManagement = () => {
    useRedirectLoggedOutUser('/login');
    const navigate = useNavigate();

    const { user } = useSelector((state) => state.auth);
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Search and Filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [showVisibleOnly, setShowVisibleOnly] = useState(false);

    // Form fields
    const [shiftBookingLocation, setShiftBookingLocation] = useState('');
    const [shiftBookingShifts, setShiftBookingShifts] = useState([]);
    const [displayName, setDisplayName] = useState('');
    const [byDefault, setByDefault] = useState('hide');
    const [availableAllTime, setAvailableAllTime] = useState(false);
    const [conversationType, setConversationType] = useState('');
    const [settings, setSettings] = useState([]);
    const [shifts, setShifts] = useState([]);

    // Fetch all users on mount
    useEffect(() => {
        fetchUsers();
        fetchSettings();
        fetchShifts();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/settings`,
                { withCredentials: true }
            );
            setSettings(data);
        } catch (error) {
            toast.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const fetchShifts = async (locationId = '') => {
        setLoading(true);
        try {
            const url = locationId
                ? `${process.env.REACT_APP_BACKEND_URL}/api/shifts?location=${locationId}`
                : `${process.env.REACT_APP_BACKEND_URL}/api/shifts`;
            const { data } = await axios.get(url, { withCredentials: true });
            setShifts(data);
        } catch (error) {
            console.error('Failed to load shifts:', error);
            // toast.error('Failed to load shifts');
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/users`,
                { withCredentials: true }
            );
            // Filter to show only staff users (non-students)
            const staffUsers = data.filter(u =>
                u.type === 'staff' && u.status === 'active'
            );
            setUsers(staffUsers);
        } catch (error) {
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    // Filter users based on search term and visibility toggle
    const filteredUsers = users.filter(user => {
        const matchesSearch = (
            user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        const matchesVisibility = showVisibleOnly ? user.byDefault === 'show' : true;

        return matchesSearch && matchesVisibility;
    });

    const handleUserSelect = (userData) => {
        setSelectedUser(userData);
        // Populate form with user's current values
        setShiftBookingLocation(userData.shiftBookingLocation || '');
        setShiftBookingShifts(userData.shiftBookingShifts?.map(s => (s._id || s).toString()) || []);
        setDisplayName(userData.displayName || '');
        setByDefault(userData.byDefault || 'hide');
        setAvailableAllTime(userData.availableAllTime || false);
        setConversationType(userData.conversationType || '');

        // Fetch shifts for this user's location if it exists
        if (userData.shiftBookingLocation) {
            fetchShifts(userData.shiftBookingLocation);
        } else {
            setShifts([]);
        }
    };

    const handleLocationChange = (locationId) => {
        setShiftBookingLocation(locationId);

        // Restore original shifts if switching back to the saved location, otherwise reset
        if (selectedUser && locationId === selectedUser.shiftBookingLocation) {
            setShiftBookingShifts(selectedUser.shiftBookingShifts?.map(s => (s._id || s).toString()) || []);
        } else {
            setShiftBookingShifts([]); // Reset shifts when location changes to a new one
        }

        if (locationId) {
            fetchShifts(locationId);
        } else {
            setShifts([]);
        }
    };

    const toggleShiftSelection = (shiftId) => {
        const idStr = shiftId.toString();
        setShiftBookingShifts(prev =>
            prev.includes(idStr)
                ? prev.filter(id => id !== idStr)
                : [...prev, idStr]
        );
    };

    const handleSave = async () => {
        if (!selectedUser) return;

        setSaving(true);
        try {
            await axios.patch(
                `${process.env.REACT_APP_BACKEND_URL}/api/users/${selectedUser._id}`,
                {
                    shiftBookingLocation,
                    shiftBookingShifts,
                    displayName,
                    byDefault,
                    availableAllTime,
                    conversationType: conversationType.trim()
                },
                { withCredentials: true }
            );

            toast.success('Configuration saved successfully');
            fetchUsers();

            // Update selected user locally to refresh the current values display
            setSelectedUser(prev => ({
                ...prev,
                shiftBookingLocation,
                shiftBookingShifts,
                displayName,
                byDefault,
                availableAllTime,
                conversationType: conversationType.trim()
            }));
        } catch (error) {
            console.error('Failed to save configuration:', error);
            toast.error(error.response?.data?.message || 'Failed to save configuration');
        } finally {
            setSaving(false);
        }
    };

    const getLocationName = (locationId) => {
        if (!locationId) return 'Not set';
        let foundTitle = locationId;
        settings.forEach(setting => {
            if (setting.locations) {
                const found = setting.locations.find(loc => (loc._id === locationId) || (loc.id === Number(locationId)));
                if (found) foundTitle = found.title;
            }
        });
        return foundTitle;
    };

    return (
        <div className="location-management-page">
            <div className="page-header">
                <button className="back-btn" onClick={() => navigate('/')}>
                    ← Back to Chat
                </button>
                <h2>User Location Management</h2>
            </div>

            <div className="user-management-container">
                <div className="user-management-content">
                    {/* User List */}
                    <div className="user-list-panel">
                        <div className="panel-header">
                            <h4>Staff Users ({filteredUsers.length})</h4>
                            <div className="search-filter-controls">
                                <input
                                    type="text"
                                    placeholder="Search by name or email..."
                                    className="form-control search-input"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <div className="filter-checkbox">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={showVisibleOnly}
                                            onChange={(e) => setShowVisibleOnly(e.target.checked)}
                                        />
                                        Show Visible Only
                                    </label>
                                </div>
                            </div>
                        </div>
                        {loading ? (
                            <p>Loading users...</p>
                        ) : (
                            <div className="user-list">
                                {filteredUsers.map((u) => (
                                    <div
                                        key={u._id}
                                        className={`user-item ${selectedUser?._id === u._id ? 'active' : ''}`}
                                        onClick={() => handleUserSelect(u)}
                                    >
                                        <img src={u.picture} alt={u.name} />
                                        <div className="user-info">
                                            <strong>{u.name}</strong>
                                            <small>{u.email}</small>
                                            {u.byDefault === 'show' && (
                                                <span className="badge badge-success">Visible</span>
                                            )}
                                            {u.availableAllTime && (
                                                <span className="badge badge-primary">Always Available</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Edit Form */}
                    <div className="user-edit-panel">
                        {selectedUser ? (
                            <>
                                <h4>Edit Location Fields - {selectedUser.name}</h4>

                                <div className="form-group">
                                    <label>Shift Booking Location</label>
                                    <select
                                        className="form-control"
                                        value={shiftBookingLocation}
                                        onChange={(e) => handleLocationChange(e.target.value)}
                                    >
                                        <option value="">Select Location</option>
                                        {settings.map((setting) =>
                                            setting.locations
                                                .filter((loc) => loc.status === "active")
                                                .map((loc) => (
                                                    <option key={loc._id} value={loc._id}>
                                                        {loc.title}
                                                    </option>
                                                ))
                                        )}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Allocated Shifts (Select Multiple)</label>
                                    <div className="shifts-grid">
                                        {shifts.length > 0 ? (
                                            shifts.map((shift) => (
                                                <div
                                                    key={shift._id}
                                                    className={`shift-selection-card ${shiftBookingShifts.includes(shift._id.toString()) ? 'selected' : ''}`}
                                                    onClick={() => toggleShiftSelection(shift._id)}
                                                >
                                                    <div className="shift-header">
                                                        <h5 className="shift-name">{shift.name}</h5>
                                                        <div className="shift-checkbox"></div>
                                                    </div>
                                                    <div className="shift-time-info">
                                                        <span className="clock-icon">🕒</span>
                                                        <span>{shift.startTime} - {shift.endTime}</span>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-muted" style={{ gridColumn: '1/-1', textAlign: 'center', padding: '20px', background: '#f8fafc', borderRadius: '8px' }}>
                                                Select a location to see available shifts
                                            </p>
                                        )}
                                    </div>
                                </div>


                                <div className="form-group">
                                    <label>Display Name (Optional)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={displayName}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        placeholder="e.g., Park Royal, Online Group 1"
                                    />
                                    <small className="form-text">
                                        Name shown in contact list (defaults to user name if empty)
                                    </small>
                                </div>

                                <div className="form-group">
                                    <label>Conversation Type</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={conversationType}
                                        onChange={(e) => setConversationType(e.target.value.toLowerCase().replace(/\s/g, ''))}
                                        placeholder="e.g., online, support"
                                    />
                                    <small className="form-text">
                                        Type categorization (e.g., support, Online, Park Royal)
                                    </small>
                                </div>

                                <div className="form-group">
                                    <label>Visible to Students</label>
                                    <div className="toggle-buttons">
                                        <button
                                            className={`toggle-btn ${byDefault === 'show' ? 'active' : ''}`}
                                            onClick={() => setByDefault('show')}
                                        >
                                            Show
                                        </button>
                                        <button
                                            className={`toggle-btn ${byDefault === 'hide' ? 'active' : ''}`}
                                            onClick={() => setByDefault('hide')}
                                        >
                                            Hide
                                        </button>
                                    </div>
                                    <small className="form-text">
                                        If "Show", user appears in contact list
                                    </small>
                                </div>

                                <div className="form-group">
                                    <label>Available All Time</label>
                                    <div className="toggle-buttons">
                                        <button
                                            className={`toggle-btn ${availableAllTime ? 'active' : ''}`}
                                            onClick={() => setAvailableAllTime(true)}
                                        >
                                            Yes
                                        </button>
                                        <button
                                            className={`toggle-btn ${!availableAllTime ? 'active' : ''}`}
                                            onClick={() => setAvailableAllTime(false)}
                                        >
                                            No
                                        </button>
                                    </div>
                                    <small className="form-text">
                                        If "Yes", user is always available even without shift booking match (fallback user)
                                    </small>
                                </div>

                                <div className="form-actions">
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleSave}
                                        disabled={saving}
                                    >
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>

                                {/* Current Values Display */}
                                <div className="current-values">
                                    <h5>Current Configuration:</h5>
                                    <table className="table table-sm">
                                        <tbody>
                                            <tr>
                                                <td><strong>Shift Booking Location:</strong></td>
                                                <td>{getLocationName(selectedUser.shiftBookingLocation)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Allocated Shifts:</strong></td>
                                                <td>{selectedUser.shiftBookingShifts?.length || 0} shift(s) selected</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Conversation Type:</strong></td>
                                                <td>{selectedUser.conversationType || <em>Not set (Online)</em>}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Display Name:</strong></td>
                                                <td>{selectedUser.displayName || <em>Not set</em>}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>By Default:</strong></td>
                                                <td>
                                                    <span className={`badge ${selectedUser.byDefault === 'show' ? 'badge-success' : 'badge-secondary'}`}>
                                                        {selectedUser.byDefault || 'hide'}
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td><strong>Available All Time:</strong></td>
                                                <td>
                                                    <span className={`badge ${selectedUser.availableAllTime ? 'badge-primary' : 'badge-secondary'}`}>
                                                        {selectedUser.availableAllTime ? 'Yes' : 'No'}
                                                    </span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <div className="no-selection">
                                <p>Select a staff user from the list to edit their location configuration</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserLocationManagement;
